import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import { isDevEnv } from 'utilities/consts/webpack-consts.js';
import { getWindow } from 'utilities/window.js';

type CoreRum = Omit<RumInitConfiguration, 'applicationId' | 'clientToken'>;

const DD_USER_ID = 'user_id';

/** Has it got datadog config props to properly initialise */
const predicateRumConfig = (
  rumConfig: RumInitConfiguration | CoreRum | undefined,
): rumConfig is RumInitConfiguration => {
  const hasAppId = Object.prototype.hasOwnProperty.call(rumConfig, 'applicationId');
  const hasClientToken = Object.prototype.hasOwnProperty.call(rumConfig, 'clientToken');
  if (hasAppId && hasClientToken) {
    return true;
  }
  return false;
};

/** Get DataDog config */
const mergeServerSideDDConfig = (): RumInitConfiguration | undefined => {
  /** Get AppId and Client token from Server Side Include */
  const ddSIConfig = getWindow()?.ddConfig ?? {};
  if (predicateRumConfig(ddSIConfig)) {
    return {
      trackViewsManually: true,
      site: 'datadoghq.com',
      version: __VERSION__,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // trackViewsManually: true,
      defaultPrivacyLevel: 'mask-user-input',
      service: 'metservice.com.front-end',
      ...ddSIConfig,

      env: isDevEnv(false) ? 'development' : (ddSIConfig?.env ?? 'unknown'),
      sessionSampleRate: ddSIConfig?.sessionSampleRate ?? 0,
      sessionReplaySampleRate: ddSIConfig?.sessionReplaySampleRate ?? 0,
    };
  }
  return undefined;
};

export const ddConfigInit = async () => {
  if (__BUILD_ENV__ !== 'development') {
    /** Initialise DataDog if Server Side Include has application and client token */
    const ddConfig = mergeServerSideDDConfig();
    const win = getWindow();
    if (win) {
      if (ddConfig) {
        const url = new URL(win.location.href);
        const userTrackingId = url.searchParams.get(DD_USER_ID);
        if (userTrackingId) {
          /** Find DD cookie and clear it if it has content */
          const matchingDDCookie = win.document.cookie.split('; ').find((item) => item.startsWith('_dd_s='));
          if (matchingDDCookie && matchingDDCookie.length > 6) {
            win.document.cookie = '_dd_s=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          }

          /** This allows a user to put in a user name ms.com?user_id=testuser in the url and have it automatically tracked */
          datadogRum.init({ ...ddConfig, sessionSampleRate: 100, sessionReplaySampleRate: 100 });

          datadogRum.setUser({
            id: userTrackingId,
          });
        } else {
          datadogRum.init({ ...ddConfig });
        }
      }
    }
  }
};
